import {
	FLO_LINK_COPIED_MESSAGE,
	FLO_PAGE_SHARE_FLO_TOOLTIP,
	FLO_PAGE_SHARE_LINK_HEADING,
	FLO_PAGE_SHARE_LINK_POPUP_CLOSE_TOOLTIP,
	FLO_PAGE_SHARE_LINK_TOOLTIP,
} from '../../Common/Common.constants';
import { IconButton, Tooltip, TooltipProps } from '@mui/material';
import { IconShare } from '@tabler/icons-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { get } from 'lodash';
import { setSuccessToast } from '../Notification/Toasts.reducers';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import styles from './ShareFloButton.module.css';

export const useShareFloClick = ({
	onShareClick,
	floId,
	floIndex,
}: {
	onShareClick?: Function;
	floId: string;
	floIndex: number;
}) => {
	const dispatch = useDispatch();
	// @ts-ignore
	const { endUserUrl, domain } = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails`, {})
	);
	const [showCopyShareUrl, setShowCopyShareUrl] = useState(false);
	const link = `${domain}/${endUserUrl}`;

	const onClick = useCallback(() => {
		onShareClick?.();
		navigator.permissions
			// @ts-ignore
			.query({ name: 'clipboard-write' })
			.then((result) => {
				if (result.state === 'granted' || result.state === 'prompt') {
					navigator.clipboard.writeText(link).then(() => {
						dispatch(
							setSuccessToast({
								data: {
									message: FLO_LINK_COPIED_MESSAGE,
								},
								floIndex,
							})
						);
					});
				} else {
					setShowCopyShareUrl(true);
				}
			});
	}, [link, onShareClick]);

	const shareAllowed = endUserUrl && domain;

	return {
		onClick,
		shareAllowed,
		showCopyShareUrl,
		link,
		setShowCopyShareUrl,
	};
};

const ShareFloButton = ({
	classes,
	tooltipProps,
	iconProps,
	isDocument,
	onShareClick,
	floId,
	floIndex,
}: {
	classes?: {
		shareButton?: string;
		shareIcon?: string;
		tooltip?: string;
	};
	iconProps?: { size?: number; className?: string };
	tooltipProps?: Partial<TooltipProps>;
	isDocument?: boolean;
	onShareClick?: Function;
	floId: string;
	floIndex: number;
}) => {
	// @ts-ignore
	const { onClick, shareAllowed, showCopyShareUrl, link, setShowCopyShareUrl } =
		useShareFloClick({ onShareClick, floId, floIndex });
	const container = document.querySelector(
		isDocument
			? '[class*=Flo_gridContainer]'
			: '.floMainVideoPlayer > [class*=customControls]'
	);
	const linkInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (showCopyShareUrl) {
			const interval = setInterval(() => {
				const inputEl = document.getElementById(
					'floLinkCopyDialogInput'
				) as HTMLInputElement;
				if (inputEl) {
					inputEl.focus();
					inputEl.setSelectionRange(0, link.length);
					clearInterval(interval);
				}
			}, 200);
		}
	}, [showCopyShareUrl]);

	const handleClose = useCallback(() => {
		setShowCopyShareUrl(false);
	}, [setShowCopyShareUrl]);

	const onDialogClick = useCallback(
		(event: React.MouseEvent) => {
			const target = event.target as HTMLElement;
			if (target.tagName === 'INPUT') return;
			handleClose();
		},
		[handleClose]
	);

	if (!shareAllowed) {
		return <></>;
	}
	const popup = (
		<div
			className={classNames(styles.dialogWrapper, { [styles.isDocument]: isDocument })}
			onClick={onDialogClick}
		>
			<Tooltip
				title={FLO_PAGE_SHARE_LINK_POPUP_CLOSE_TOOLTIP}
				arrow
				classes={{
					popper: classNames(styles.tooltip, classes?.tooltip),
				}}
			>
				<IconButton
					className={styles.closeButton}
					aria-label="close"
					onClick={handleClose}
					onTouchEnd={handleClose}
					sx={{
						position: 'absolute',
						right: 24,
						top: 24,
					}}
				>
					<CloseIcon className={styles.closeIcon} />
				</IconButton>
			</Tooltip>
			<div className={styles.dialogContent}>
				<div className={styles.dialogHeading}>{FLO_PAGE_SHARE_LINK_HEADING}</div>
				<Tooltip
					title={FLO_PAGE_SHARE_LINK_TOOLTIP}
					arrow
					classes={{
						popper: classNames(styles.tooltip, classes?.tooltip),
					}}
				>
					<input
						className={styles.linkTextInput}
						id="floLinkCopyDialogInput"
						type="text"
						value={link}
						ref={linkInputRef}
					></input>
				</Tooltip>
			</div>
		</div>
	);
	return (
		<>
			{showCopyShareUrl && container && createPortal(popup, container)}
			<Tooltip
				classes={{
					popper: classNames(styles.tooltip, classes?.tooltip),
				}}
				arrow
				title={FLO_PAGE_SHARE_FLO_TOOLTIP}
				{...tooltipProps}
			>
				<IconButton
					className={classNames(styles.shareButton, classes?.shareButton)}
					onClick={onClick}
				>
					{/*@ts-ignore*/}
					<IconShare size={18} className={classes?.shareIcon} {...iconProps} />
				</IconButton>
			</Tooltip>
		</>
	);
};

export default ShareFloButton;
